import { useState } from "react";
import {
  Checkbox,
  DialogContent,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import * as Yup from "yup";
import { Formik } from "formik";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDInput from "components/MDInput";
import moment from "moment";
import { useMaterialUIController } from "context";
import { useDispatch } from "react-redux";
import CircularIndeterminate from "components/MDLoading";
import MDSnackbar from "components/MDSnackbar";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  createAndUpdateBooting,
  getBootings,
} from "store/slice/bootingRevenue/bootingRevenueSlice";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const CreateBooting = (props) => {
  const [controller] = useMaterialUIController();
  const dispatch = useDispatch();
  const { sidenavColor } = controller;
  const {
    dialogOpen,
    onClose,
    placeId,
    isEdit = false,
    bootingData,
    tz,
    placesList,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification((prev) => ({ ...prev, show: false })),
  });

  const createBootingSchema = Yup.object().shape({
    bootingDate: Yup.date().required("Booting date is required"),
    registerBooting: Yup.number()
      .required("Register Booting is required")
      .min(0, "Register Booting must be at least 0")
      .integer("Register Booting must be an integer"),
    vipBooting: Yup.number()
      .required("Vip Booting is required")
      .min(0, "Vip Booting must be at least 0")
      .integer("Vip Booting must be an integer"),
    registerBootingAmount: Yup.number().when("registerBooting", {
      is: (registerBooting) => registerBooting > 0,
      then: () =>
        Yup.number()
          .required("Register Booting Amount is required")
          .min(0.01, "Register Booting Amount must be greater than 0"),
      otherwise: () => Yup.number().nullable(),
    }),
    vipBootingAmount: Yup.number().when("vipBooting", {
      is: (vipBooting) => vipBooting > 0,
      then: () =>
        Yup.number()
          .required("VIP Booting Amount is required")
          .min(0.01, "VIP Booting Amount must be greater than 0"),
      otherwise: () => Yup.number().nullable(),
    }),
  });

  const handleStartDateChange = (newValue, setFieldValue) => {
    setFieldValue("bootingDateObj", newValue);
    setFieldValue("bootingDate", newValue ? moment(newValue) : null);
  };

  const handleCreateBooting = (data) => {
    setIsLoading(true);

    let payload = {
      ...data,
      placeIds: data.isAllLocations ? placesList.map((p) => p._id) : [placeId],
      tz,
      bootingDate: moment(data.bootingDate).format("YYYY-MM-DD").toString(),
      registerBootingAmount: data.registerBootingAmount
        ? data.registerBootingAmount * 100
        : null,
      vipBootingAmount: data.vipBootingAmount
        ? data.vipBootingAmount * 100
        : null,
    };

    if (isEdit) {
      payload._id = bootingData._id;
    }

    dispatch(createAndUpdateBooting(payload))
      .unwrap()
      .then((res) => {
        const success = res?.success;
        setIsLoading(false);
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
        if (success) {
          dispatch(getBootings(placeId));
          onClose();
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message,
          icon: "warning",
          show: true,
        });
      });
  };

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Creating Booting..."
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle={`${isEdit ? "Edit" : "Create"} Booting`}
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon
        maxWidth="sm"
      >
        <DialogContent>
          <Formik
            initialValues={{
              placeIds: bootingData?.isAllLocations
                ? placesList?.map((p) => p._id)
                : [placeId],
              splitType: bootingData?.splitType || "equalDivide",
              isAllLocations: bootingData?.isAllLocations || false,
              bootingDate: bootingData?.bootingDate
                ? moment(bootingData.bootingDate)
                : null,
              bootingDateObj: bootingData?.bootingDate
                ? moment(bootingData.bootingDate)
                : null,
              registerBooting: bootingData?.registerBooting || 0,
              vipBooting: bootingData?.vipBooting || 0,
              registerBootingAmount: bootingData?.registerBootingAmount
                ? bootingData.registerBootingAmount / 100
                : null,
              vipBootingAmount: bootingData?.vipBootingAmount
                ? bootingData.vipBootingAmount / 100
                : null,
            }}
            validationSchema={createBootingSchema}
            onSubmit={handleCreateBooting}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} className="mt-1">
                  <Grid item xs={12}>
                    <DesktopDatePicker
                      label="Booting Date"
                      value={values?.bootingDateObj}
                      onChange={(newValue) => {
                        handleStartDateChange(newValue, setFieldValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: touched.bootingDate && !!errors.bootingDate,
                          helperText: touched.bootingDate && errors.bootingDate,
                        },
                      }}
                      format="MM/DD/YYYY"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="registerBooting"
                      type="number"
                      value={values.registerBooting}
                      onChange={(e) => {
                        handleChange(e);
                        if (parseInt(e.target.value) === 0 || !e.target.value) {
                          setFieldValue("registerBootingAmount", null);
                        }
                      }}
                      onBlur={handleBlur}
                      label="Register Booting"
                      fullWidth
                      error={
                        touched.registerBooting && !!errors.registerBooting
                      }
                      helperText={
                        touched.registerBooting && errors.registerBooting
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="registerBootingAmount"
                      type="number"
                      value={values.registerBootingAmount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Register Booting Amount"
                      fullWidth
                      sx={{
                        "&.MuiFormControl-root": {
                          backgroundColor: "transparent !important",
                        },
                      }}
                      error={
                        touched.registerBootingAmount &&
                        !!errors.registerBootingAmount
                      }
                      helperText={
                        touched.registerBootingAmount &&
                        errors.registerBootingAmount
                      }
                      inputProps={{ min: 0.01, step: 0.01 }}
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: 4 }}>$</span>
                        ),
                      }}
                      disabled={
                        values.registerBooting === 0 || !values.registerBooting
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="vipBooting"
                      type="number"
                      value={values.vipBooting}
                      onChange={(e) => {
                        handleChange(e);
                        if (parseInt(e.target.value) === 0 || !e.target.value) {
                          setFieldValue("vipBootingAmount", null);
                        }
                      }}
                      onBlur={handleBlur}
                      label="VIP Booting"
                      fullWidth
                      error={touched.vipBooting && !!errors.vipBooting}
                      helperText={touched.vipBooting && errors.vipBooting}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="vipBootingAmount"
                      type="number"
                      value={values.vipBootingAmount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="VIP Booting Amount"
                      fullWidth
                      sx={{
                        "&.MuiFormControl-root": {
                          backgroundColor: "transparent !important",
                        },
                      }}
                      error={
                        touched.vipBootingAmount && !!errors.vipBootingAmount
                      }
                      helperText={
                        touched.vipBootingAmount && errors.vipBootingAmount
                      }
                      inputProps={{ min: 0.01, step: 0.01 }}
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: 4 }}>$</span>
                        ),
                      }}
                      disabled={values.vipBooting === 0 || !values.vipBooting}
                    />
                  </Grid>

                  {errors["at-least-one-valid-pair"] && (
                    <Grid item xs={12}>
                      <div style={{ color: "red", fontSize: "0.75rem" }}>
                        {errors["at-least-one-valid-pair"]}
                      </div>
                    </Grid>
                  )}
                  {!isEdit && (
                    <Grid item xs={12}>
                      <MDBox display="flex" alignItems="center" ml={-1}>
                        <Checkbox
                          id="allLocations"
                          name="isAllLocations"
                          checked={values.isAllLocations}
                          onChange={(e) => {
                            handleChange(e);
                            setFieldValue("isAllLocations", e.target.checked);
                          }}
                          inputProps={{ "aria-label": "controlled" }}
                        />
                        <MDTypography
                          variant="button"
                          fontWeight="regular"
                          color="text"
                        >
                          &nbsp;&nbsp;Would you like to assign this to all
                          locations?&nbsp;
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}

                  {values.isAllLocations && (
                    <Grid item xs={12}>
                      <RadioGroup
                        name="splitType"
                        sx={{ flexDirection: "row" }}
                        value={values.splitType}
                        onChange={(e) =>
                          setFieldValue("splitType", e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="divideEqually"
                          control={<Radio color="primary" />}
                          label="Divide equally"
                        />
                        <FormControlLabel
                          value="sameForAllLocations"
                          control={<Radio />}
                          label="Assign same for all locations"
                        />
                      </RadioGroup>
                    </Grid>
                  )}

                  <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <MDButton
                      color={sidenavColor}
                      variant="contained"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isEdit ? "Update" : "Create"}
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default CreateBooting;
